<script setup lang="ts">
  import { useTalkRoomStore } from "~/stores/TalkRoomStore";
  import { useSnackBarStore } from "~/stores/SnackBarStore";
  import type { Talk } from "~/entities/Talk";
  import TalkInputMessage from "~/components/Organisms/TalkInputMessage.vue";

  // components
  const talkInputMessage = ref<InstanceType<typeof TalkInputMessage> | null>(null);
  const sendBtn = ref();

  // stores
  const talkRoomStore = useTalkRoomStore();
  const snackBarStore = useSnackBarStore();

  interface Props {
    open: boolean;
    talk: Talk;
    isTest?: boolean; // テスト時のみ true にする
  }
  const props = defineProps<Props>();

  interface Emits {
    (event: "update:open", open: boolean): void;
    (event: "send"): void;
  }
  const emit = defineEmits<Emits>();
  const isOpen = computed<boolean>({
    get: () => props.open,
    set: value => {
      emit("update:open", value);
    },
  });

  // 送信可否フラグ
  const canSend = ref<boolean>(false);

  // 送信中フラグ
  const isSending = ref<boolean>(false);

  const closeDialog = () => {
    isOpen.value = false;
  };

  const sendFunc = async (message: string, mentionUserIds: string[]) => {
    await talkRoomStore.editTalk(props.talk.talkId, message, mentionUserIds);
  };
  const onSend = () => {
    talkInputMessage.value?.triggerSend();
  };
  const onSent = () => {
    snackBarStore.set("メッセージを更新しました");
    emit("send");
    closeDialog();
  };
  const onFailed = (error: any) => {
    sentryErrorLog(`トークメッセージ更新失敗 ${error}`);
    snackBarStore.setError("メッセージの更新に失敗しました");
  };

  // レスポンシブ対応
  const isSp = isSpDisplayRef();
</script>

<template>
  <AtomsOverlay v-model:loading="isSending" />
  <v-dialog v-model="isOpen" scrim="black" :attach="!!isTest">
    <v-sheet class="dialog py-8 rounded" :class="isSp ? 'px-4' : 'px-10'" color="monochrome-04" align="center">
      <div class="text-dialog-title">メッセージを更新しますか？</div>

      <OrganismsTalkInputMessage
        ref="talkInputMessage"
        v-model:can-send="canSend"
        v-model:is-sending="isSending"
        class="mt-8 mb-4"
        :talk-to-edit="talk"
        :allow-empty-send="false"
        placeholder="メッセージを入力..."
        :fixed-position="true"
        :send-func="sendFunc"
        @sent="onSent"
        @failed="onFailed"
      />

      <div class="d-flex justify-space-evenly flex-wrap">
        <AtomsDialogButton
          :disabled="isSending"
          color="monochrome-02"
          class="button mt-5"
          data-testid="talk-edit-cancel"
          @click="closeDialog"
          >キャンセル
        </AtomsDialogButton>
        <AtomsDialogButton
          ref="sendBtn"
          :loading="isSending"
          :disabled="!canSend"
          color="main-01"
          class="button mt-5"
          data-testid="talk-edit-send"
          @click="onSend"
          >送信</AtomsDialogButton
        >
      </div>
    </v-sheet>
  </v-dialog>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";

  // TODO TalkMessage と類似。コンポーネント化したい
  .dialog {
    width: 600px;
    max-width: calc(100% - 40px);
    min-height: 146px;
    min-width: 345px;
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    -webkit-transform: translate(-50%, -50%);
    -ms-transform: translate(-50%, -50%);
    max-height: 100vh;
    overflow-y: auto;
  }
</style>
